import { memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicFiltersBar } from '~/components/Filters/v2/components/PublicFiltersBar';
import { type PrivateSearchProps } from '~/components/Search/PrivateSearch';
import { PublicSearchActions } from '~/components/Search/PublicSearchActions';
import { useBoardSearchToken } from '~/components/Search/SearchTokens/useBoardSearchToken';
import { type SearchBarHandle } from '~/components/SearchBar/SearchBar';
import { type BoardSuggestionType } from '~/components/SearchBar/types';
import { MobileSearchCommand } from '~/components/SearchCommand/MobileSearchCommand';
import { SearchCommand } from '~/components/SearchCommand/SearchCommand';
import { createShortUrlBoardRoute } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useStickyResult } from '~/hooks/useStickyResult';
import { type PublicBoardContextValue } from '~/providers/PublicBoardProvider';
import { centralizedBoardAncestorsSelector, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { usePublicSearchSuggestions } from '~/swr-hooks/search/usePublicSearchSuggestions';
import { pushWithExistingQuery } from '~/utils/PathUtils';

export type PublicSearchProps = {
  onCloseClick: () => void;
  /**
   * Navigate the viewer into a clicked board suggestion. Search renders on both board trees (the
   * v2 page header and the legacy <medium header), so the drill arrives as a prop from whichever
   * tree hosts the search bar rather than a contract read here.
   */
  onNavigateToBoard: PublicBoardContextValue['onNavigateToBoard'];
};

export const PublicSearch = memo(({ onCloseClick, onNavigateToBoard }: PublicSearchProps) => {
  const { filtersCount } = useAppliedFiltersCount();
  const [searchTerm, setSearchTerm] = useState('');
  const { isAboveMediumScreen } = useBreakpointsContext();

  const { shortId } = useShortIdContext();
  const boardName = useSelector(centralizedBoardTitleSelector);
  const boardAncestors = useSelector(centralizedBoardAncestorsSelector);

  const searchBarRef = useRef<SearchBarHandle>(null);

  const [isFocused, setIsFocused] = useState(false);

  const { search, setSearch, clearSearch } = useSearchParams();

  const onClose = useCallback(() => {
    clearSearch();
    onCloseClick();
  }, [clearSearch, onCloseClick]);

  // KNOWN QUESTIONABLE, preserved verbatim: search covers the WHOLE share link, but a current-page
  // drill stamps the viewer's page onto the route (`?page={slug}`) — a result from another page
  // gets a breadcrumb trail claiming it lives where the viewer was standing. The designed behavior
  // (filed showcase-search bugs, tickets pending): searching jumps back to top level and results
  // render on a plain results page, so a clicked result carries NO trail — when that lands, pass
  // `page: { pageId: null, pageSlug: null }` here explicitly.
  const onBoardClick = useCallback(
    (board: BoardSuggestionType) => onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
    [onNavigateToBoard],
  );

  const { data, refetch } = usePublicSearchSuggestions({ search: searchTerm, shortId, canFetch: isFocused });
  // this hook prevents passing empty data to SearchBar while suggestions are loading
  // It is used to keep previous suggestions visible while new ones are not returned yet
  const suggestions = useStickyResult(data);

  const onFocusChange = useCallback(
    (isFocused: boolean) => {
      setIsFocused(isFocused);
      if (isFocused) {
        refetch();
      }
    },
    [refetch],
  );

  const { wasTokenRemoved } = useBoardSearchToken({
    isFocused,
    hasValue: !!searchTerm,
    boardName,
    searchBarRef,
  });

  const onApplyTextSearch = useCallback(
    (newTerm: string) => {
      if (wasTokenRemoved) {
        const newQuery = { [QueryParamNames.Search]: newTerm };

        if (boardAncestors.length > 0) {
          pushWithExistingQuery({
            path: shortId ? createShortUrlBoardRoute(shortId, boardAncestors[0].id) : '/',
            newQuery,
          });
        } else {
          pushWithExistingQuery({
            newQuery,
          });
        }
      } else {
        void setSearch(newTerm);
      }
      setSearchTerm(newTerm);
    },
    [boardAncestors, wasTokenRemoved, setSearch, shortId],
  );

  const renderActions = useCallback<Required<PrivateSearchProps>['renderActions']>(({ onClearSearchValue }) => {
    return <PublicSearchActions onClearSearchValue={onClearSearchValue} />;
  }, []);

  if (isAboveMediumScreen) {
    return (
      <SearchCommand
        filterBar={isAboveMediumScreen && <PublicFiltersBar trackLocation="search" />}
        onBoardClick={onBoardClick}
        onClear={isAboveMediumScreen ? undefined : onClose}
        onOpenChange={onFocusChange}
        onSearchChange={setSearchTerm}
        onSuggestionClick={onApplyTextSearch}
        placeholder={boardName ? `Search ${boardName}` : 'Search board'}
        search={search}
        suggestions={suggestions}
        renderActions={renderActions}
      />
    );
  }

  return (
    <MobileSearchCommand
      filtersCount={isAboveMediumScreen ? filtersCount : undefined}
      onBoardClick={onBoardClick}
      onClear={isAboveMediumScreen ? undefined : onClose}
      onOpenChange={onFocusChange}
      onSearchChange={setSearchTerm}
      onSuggestionClick={onApplyTextSearch}
      placeholder="Search board"
      suggestions={suggestions}
    />
  );
});

PublicSearch.displayName = 'PublicSearch';
